/**
 * Per-content-type rule tables and client-side violation detection.
 *
 * The backend source of truth is `backend/app/services/validation/preflight.py`
 * (`PLATFORM_RULES`). These client-side rules mirror a user-visible subset
 * across the major platforms (Instagram / Facebook / TikTok / YouTube /
 * LinkedIn) so the composer can:
 *
 *   - Show the user which limits apply to the selected content type
 *   - Surface obvious violations instantly (no server round-trip)
 *
 * Anything that depends on media metadata we don't have on the client
 * (aspect ratio, video duration, file size) is detected by the backend
 * preflight endpoint and returned separately. This module does NOT try
 * to duplicate those checks.
 */
import type { StudioPostState } from '@/store/studioStore';

export type ContentType = StudioPostState['content_type'];

export interface ContentTypeRule {
  /** i18n key for the rule label; falls back to `fallback`. */
  i18nKey: string;
  /** English fallback label used when no translation is present. */
  fallback: string;
  /** Optional short description shown under the label. */
  descriptionKey?: string;
  descriptionFallback?: string;
}

export interface ContentTypeRuleSet {
  /** Short headline for the selected type, shown at the top of the panel. */
  headlineKey: string;
  headlineFallback: string;
  /** Ordered list of rules to display. */
  rules: ContentTypeRule[];
}

/**
 * Display rules per content type. These are the *visible* summary of
 * what the backend preflight enforces — they don't drive validation
 * themselves (the backend does that). Keeping them here makes the panel
 * readable without a server round-trip.
 */
export const CONTENT_TYPE_RULES: Record<ContentType, ContentTypeRuleSet> = {
  post: {
    headlineKey: 'rules_post_headline',
    headlineFallback: 'Standard feed post',
    rules: [
      {
        i18nKey: 'rules_post_aspect',
        fallback: 'Aspect ratio: 4:5 to 1.91:1 (Instagram / Facebook)',
      },
      {
        i18nKey: 'rules_post_caption',
        fallback: 'Caption: up to 2,200 chars on Instagram · 280 on Twitter',
      },
      {
        i18nKey: 'rules_post_hashtags',
        fallback: 'Hashtags: up to 30 on Instagram',
      },
      {
        i18nKey: 'rules_post_video',
        fallback: 'Video (optional): 3–60s on Instagram',
      },
    ],
  },

  story: {
    headlineKey: 'rules_story_headline',
    headlineFallback: 'Vertical story — disappears after 24h',
    rules: [
      {
        i18nKey: 'rules_story_aspect',
        fallback: 'Aspect ratio: 9:16 (1080×1920) required',
      },
      {
        i18nKey: 'rules_story_media',
        fallback: 'Media: image or video required',
      },
      {
        i18nKey: 'rules_story_duration',
        fallback: 'Video: 1–60 seconds',
      },
      {
        i18nKey: 'rules_story_cards',
        fallback: 'Up to 10 cards and 10 stickers per card',
      },
      {
        i18nKey: 'rules_story_no_hashtags',
        fallback: 'Hashtags and comments don\u2019t apply to Stories',
      },
    ],
  },

  reel: {
    headlineKey: 'rules_reel_headline',
    headlineFallback: 'Short-form vertical video',
    rules: [
      {
        i18nKey: 'rules_reel_aspect',
        fallback: 'Aspect ratio: 9:16 (1080×1920) required',
      },
      {
        i18nKey: 'rules_reel_video',
        fallback: 'Video required — no image-only Reels',
      },
      {
        i18nKey: 'rules_reel_duration',
        fallback: 'Duration: 3–90 seconds on Instagram / Facebook',
      },
      {
        i18nKey: 'rules_reel_caption',
        fallback: 'Caption: up to 2,200 chars',
      },
      {
        i18nKey: 'rules_reel_hashtags',
        fallback: 'Hashtags: up to 30 on Instagram',
      },
    ],
  },

  short: {
    headlineKey: 'rules_short_headline',
    headlineFallback: 'YouTube Short — vertical video under a minute',
    rules: [
      {
        i18nKey: 'rules_short_aspect',
        fallback: 'Aspect ratio: 9:16 (1080×1920) required',
      },
      {
        i18nKey: 'rules_short_video',
        fallback: 'Video required',
      },
      {
        i18nKey: 'rules_short_duration',
        fallback: 'Duration: 1–60 seconds',
      },
      {
        i18nKey: 'rules_short_caption',
        fallback: 'Caption: up to 5,000 chars on YouTube',
      },
    ],
  },

  carousel: {
    headlineKey: 'rules_carousel_headline',
    headlineFallback: 'Multi-slide carousel (Instagram-style)',
    rules: [
      {
        i18nKey: 'rules_carousel_slides',
        fallback: 'Slides: 2 to 10 images or videos',
      },
      {
        i18nKey: 'rules_carousel_aspect',
        fallback: 'Aspect ratio: 4:5 to 1.91:1 — all slides must match',
      },
      {
        i18nKey: 'rules_carousel_caption',
        fallback: 'Caption: up to 2,200 chars',
      },
      {
        i18nKey: 'rules_carousel_hashtags',
        fallback: 'Hashtags: up to 30 on Instagram',
      },
    ],
  },

  igtv: {
    headlineKey: 'rules_igtv_headline',
    headlineFallback: 'IGTV (deprecated — now treated as Reel by Instagram)',
    rules: [
      {
        i18nKey: 'rules_igtv_aspect',
        fallback: 'Aspect ratio: 9:16 (1080×1920)',
      },
      {
        i18nKey: 'rules_igtv_video',
        fallback: 'Video required — 3–90s',
      },
      {
        i18nKey: 'rules_igtv_deprecated',
        fallback: 'IGTV was folded into Reels in 2022 — prefer Reel',
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Client-side violations
// ---------------------------------------------------------------------------

export type Severity = 'error' | 'warning';

export interface ClientViolation {
  id: string;
  severity: Severity;
  messageKey: string;
  messageFallback: string;
  /** i18n variable bag for interpolation (e.g. `{{count}}`). */
  values?: Record<string, string | number>;
}

/** Rough upper-bound caption limit used as a warning threshold client-side. */
const GLOBAL_CAPTION_SOFT_LIMIT = 2200;

/** Rough upper-bound hashtag count across platforms. */
const GLOBAL_HASHTAG_SOFT_LIMIT = 30;

type CanonicalContentType = 'post' | 'story' | 'reel' | 'carousel';

interface PlatformContentRule {
  requiresMedia?: boolean;
  requiresVideo?: boolean;
  requiresImage?: boolean;
  minSlides?: number;
  maxSlides?: number;
}

const PLATFORM_LABELS: Record<string, string> = {
  instagram: 'Instagram',
  twitter: 'Twitter/X',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  bluesky: 'Bluesky',
};

// Frontend mirror of the user-visible subset of
// backend/app/services/media/platform_specs.py. Keep it intentionally
// compact: this module only needs support/media presence/slide-count
// checks. Exact aspect, file size, codec, and duration still belong to
// backend preflight + MediaComplianceBanner.
const PLATFORM_CONTENT_RULES: Record<string, Partial<Record<CanonicalContentType, PlatformContentRule>>> = {
  instagram: {
    post: { requiresMedia: true },
    story: { requiresMedia: true },
    reel: { requiresVideo: true },
    carousel: { requiresMedia: true, minSlides: 2, maxSlides: 10 },
  },
  facebook: {
    post: {},
    story: { requiresMedia: true },
    reel: { requiresVideo: true },
    carousel: { requiresMedia: true, minSlides: 2, maxSlides: 10 },
  },
  tiktok: {
    post: { requiresVideo: true },
    story: { requiresMedia: true },
    reel: { requiresVideo: true },
    carousel: { requiresImage: true, minSlides: 2, maxSlides: 35 },
  },
  youtube: {
    post: { requiresVideo: true },
    reel: { requiresVideo: true },
  },
  twitter: {
    post: {},
  },
  linkedin: {
    post: {},
    carousel: { requiresImage: true, minSlides: 2, maxSlides: 20 },
  },
  pinterest: {
    post: { requiresMedia: true },
  },
  bluesky: {
    post: {},
  },
};

function normalizeContentType(contentType: ContentType): CanonicalContentType {
  if (contentType === 'short' || contentType === 'igtv') {
    return 'reel';
  }
  return contentType;
}

function formatContentType(contentType: ContentType): string {
  if (contentType === 'short') {
    return 'Short';
  }
  if (contentType === 'igtv') {
    return 'IGTV';
  }
  return contentType.charAt(0).toUpperCase() + contentType.slice(1);
}

function platformLabel(platform: string): string {
  return PLATFORM_LABELS[platform] || platform.charAt(0).toUpperCase() + platform.slice(1);
}

function genericContentTypeViolations(
  contentType: ContentType,
  mediaCount: number,
  hasMedia: boolean,
  hasVideo: boolean,
): ClientViolation[] {
  const violations: ClientViolation[] = [];
  if (contentType === 'story' && !hasMedia) {
    violations.push({
      id: 'story_requires_media',
      severity: 'error',
      messageKey: 'rules_violation_story_requires_media',
      messageFallback: 'Stories require an image or video.',
    });
  }

  if ((contentType === 'reel' || contentType === 'short' || contentType === 'igtv')
      && !hasVideo) {
    violations.push({
      id: 'video_required',
      severity: 'error',
      messageKey: 'rules_violation_video_required',
      messageFallback: 'This format requires a video.',
    });
  }

  if (contentType === 'carousel') {
    if (mediaCount < 2) {
      violations.push({
        id: 'carousel_too_few_slides',
        severity: 'error',
        messageKey: 'rules_violation_carousel_too_few',
        messageFallback: 'Carousel needs at least 2 slides (you have {{count}}).',
        values: { count: mediaCount },
      });
    } else if (mediaCount > 10) {
      violations.push({
        id: 'carousel_too_many_slides',
        severity: 'error',
        messageKey: 'rules_violation_carousel_too_many',
        messageFallback: 'Carousel allows at most 10 slides (you have {{count}}).',
        values: { count: mediaCount },
      });
    }
  }
  return violations;
}

/**
 * Inspect the current in-memory post and return violations that can be
 * detected without a server round-trip. Covers: media presence, video
 * requirement, slide count, basic caption/hashtag limits.
 *
 * This is intentionally conservative — anything that requires media
 * dimensions, duration, or file size is left to the backend preflight.
 */
export function computeClientViolations(post: StudioPostState): ClientViolation[] {
  const violations: ClientViolation[] = [];
  const contentType = post.content_type;
  const media = post.media || [];
  const mediaCount = media.length;
  const hasVideo = media.some((m) => m.type === 'video');
  const hasImage = media.some((m) => m.type === 'image' || m.type === 'gif');
  const hasMedia = mediaCount > 0;
  const textLen = (post.text || '').length;
  const selectedPlatforms = post.target_platforms || [];

  if (selectedPlatforms.length === 0) {
    violations.push(
      ...genericContentTypeViolations(contentType, mediaCount, hasMedia, hasVideo),
    );
  } else {
    const canonicalType = normalizeContentType(contentType);
    const contentTypeLabel = formatContentType(contentType);

    for (const platform of selectedPlatforms) {
      const label = platformLabel(platform);
      const rule = PLATFORM_CONTENT_RULES[platform]?.[canonicalType];
      if (!rule) {
        violations.push({
          id: `unsupported_${platform}_${contentType}`,
          severity: 'error',
          messageKey: 'rules_violation_platform_unsupported',
          messageFallback:
            '{{platform}} does not support {{contentType}}. Change format or remove the platform.',
          values: { platform: label, contentType: contentTypeLabel },
        });
        continue;
      }

      if (rule.requiresVideo && !hasVideo) {
        violations.push({
          id: `video_required_${platform}_${contentType}`,
          severity: 'error',
          messageKey: 'rules_violation_platform_requires_video',
          messageFallback: '{{platform}} requires a video for {{contentType}}.',
          values: { platform: label, contentType: contentTypeLabel },
        });
        continue;
      }

      if (rule.requiresImage && !hasImage) {
        violations.push({
          id: `image_required_${platform}_${contentType}`,
          severity: 'error',
          messageKey: 'rules_violation_platform_requires_image',
          messageFallback: '{{platform}} requires images for {{contentType}}.',
          values: { platform: label, contentType: contentTypeLabel },
        });
        continue;
      }

      if (rule.requiresMedia && !hasMedia) {
        violations.push({
          id: `media_required_${platform}_${contentType}`,
          severity: 'error',
          messageKey: 'rules_violation_platform_requires_media',
          messageFallback: '{{platform}} requires media for {{contentType}}.',
          values: { platform: label, contentType: contentTypeLabel },
        });
        continue;
      }

      if (rule.minSlides && mediaCount < rule.minSlides) {
        violations.push({
          id: `carousel_too_few_slides_${platform}`,
          severity: 'error',
          messageKey: 'rules_violation_platform_carousel_too_few',
          messageFallback:
            '{{platform}} carousel needs at least {{min}} slides (you have {{count}}).',
          values: { platform: label, min: rule.minSlides, count: mediaCount },
        });
      } else if (rule.maxSlides && mediaCount > rule.maxSlides) {
        violations.push({
          id: `carousel_too_many_slides_${platform}`,
          severity: 'error',
          messageKey: 'rules_violation_platform_carousel_too_many',
          messageFallback:
            '{{platform}} carousel allows at most {{max}} slides (you have {{count}}).',
          values: { platform: label, max: rule.maxSlides, count: mediaCount },
        });
      }
    }
  }

  // ── Caption length (soft warning across platforms) ────────────
  if (textLen > GLOBAL_CAPTION_SOFT_LIMIT) {
    violations.push({
      id: 'caption_over_limit',
      severity: 'warning',
      messageKey: 'rules_violation_caption_over_limit',
      messageFallback:
        'Caption is {{count}} chars — Instagram / Facebook cut off around {{limit}}.',
      values: { count: textLen, limit: GLOBAL_CAPTION_SOFT_LIMIT },
    });
  }

  // ── Hashtag count (soft warning) ──────────────────────────────
  if ((post.hashtags || []).length > GLOBAL_HASHTAG_SOFT_LIMIT) {
    violations.push({
      id: 'too_many_hashtags',
      severity: 'warning',
      messageKey: 'rules_violation_too_many_hashtags',
      messageFallback:
        '{{count}} hashtags — Instagram caps at {{limit}}.',
      values: { count: (post.hashtags || []).length, limit: GLOBAL_HASHTAG_SOFT_LIMIT },
    });
  }

  // ── Stories: hashtags/first-comment don't render ──────────────
  if (contentType === 'story' && (post.hashtags || []).length > 0) {
    violations.push({
      id: 'story_hashtags_noop',
      severity: 'warning',
      messageKey: 'rules_violation_story_hashtags_noop',
      messageFallback: 'Hashtags don\u2019t render on Stories — they\u2019ll be ignored.',
    });
  }

  // ── Story: multiple images isn't a carousel, it's cards ───────
  if (contentType === 'story' && mediaCount > 10) {
    violations.push({
      id: 'story_too_many_cards',
      severity: 'error',
      messageKey: 'rules_violation_story_too_many_cards',
      messageFallback: 'Stories allow up to 10 cards ({{count}} selected).',
      values: { count: mediaCount },
    });
  }

  // ── IGTV deprecation notice ───────────────────────────────────
  if (contentType === 'igtv') {
    violations.push({
      id: 'igtv_deprecated',
      severity: 'warning',
      messageKey: 'rules_violation_igtv_deprecated',
      messageFallback: 'IGTV was merged into Reels in 2022 — prefer the Reel tab.',
    });
  }

  return violations;
}
